import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Facebook, Sparkles, Send, Calendar, Image as ImageIcon, Type } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Company {
  id: string;
  name: string;
  pageId: string;
}

interface PostData {
  text: string;
  imageUrl?: string;
  linkUrl?: string;
}

const companies: Company[] = [
  { id: 'halesac', name: "Hale's AC", pageId: '636778260109013' },
  { id: 'myccs', name: 'Climate Control Services', pageId: '435762533166448' },
  { id: 'abrahamac', name: 'Abraham AC', pageId: '117509004742759' }
];

export default function FacebookManagerPage() {
  const [activeTab, setActiveTab] = useState<'create' | 'analytics'>('create');
  const [selectedCompany, setSelectedCompany] = useState<Company>(companies[0]);
  const [postText, setPostText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    setCharCount(postText.length);
  }, [postText]);

  // Maginary Image Generation
  const handleGenerateImage = async () => {
    setIsGeneratingImage(true);
    try {
      const apiKey = import.meta.env.VITE_MAGINARY_API_KEY;
      if (!apiKey) {
        toast.error('Maginary API key not configured');
        return;
      }

      // Create image prompt based on company and post content
      const imagePrompt = postText 
        ? `Professional HVAC technician working on air conditioning system, ${selectedCompany.name} branding, high quality, photorealistic, bright Florida setting`
        : `Modern HVAC air conditioning unit, professional installation, ${selectedCompany.name}, clean professional photo, bright outdoor setting`;

      toast.loading('Generating image with Maginary AI... (30-120 seconds)', { id: 'maginary', duration: 120000 });

      // Step 1: Create generation
      const createRes = await fetch('https://app.maginary.ai/api/gens/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt: imagePrompt })
      });

      if (!createRes.ok) {
        throw new Error('Failed to start image generation');
      }

      const genData = await createRes.json();
      const { uuid } = genData;

      if (!uuid) {
        throw new Error('No UUID returned from Maginary');
      }

      // Step 2: Poll for completion
      let attempts = 0;
      const maxAttempts = 40; // 2 minutes max
      let imageUrl = null;

      while (attempts < maxAttempts && !imageUrl) {
        await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds

        const statusRes = await fetch(`https://app.maginary.ai/api/gens/${uuid}/`, {
          headers: {
            'Authorization': `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        });

        if (statusRes.ok) {
          const status = await statusRes.json();
          
          if (status.status === 'completed' && status.images && status.images.length > 0) {
            // Use the first generated image
            imageUrl = status.images[0].url;
            break;
          } else if (status.status === 'failed') {
            throw new Error('Image generation failed');
          }
        }

        attempts++;
      }

      if (imageUrl) {
        setImageUrl(imageUrl);
        toast.success('Image generated successfully! 🎨', { id: 'maginary' });
      } else {
        toast.error('Image generation timed out. Please try again.', { id: 'maginary' });
      }

    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image. Please try again.', { id: 'maginary' });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // AI Content Generation (proxied through backend - no client secrets)
  const handleGenerateContent = async () => {
    setIsGenerating(true);
    try {
      const apiBase = import.meta.env.VITE_MARKETING_API_URL;
      if (!apiBase) {
        toast.error('Backend API URL not configured');
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toast.error('Not authenticated. Please log in again.');
        return;
      }

      const prompt = `Generate an engaging Facebook post for ${selectedCompany.name}, an HVAC company in Florida.

Requirements:
- Keep it under 500 characters
- Include a call-to-action
- Use 1-2 relevant emojis
- Focus on seasonal HVAC tips, energy savings, or service reminders
- Professional yet friendly tone
- No hashtags (Facebook doesn't prioritize them)

Output JSON only with this exact shape:
{ "text": "<the generated post text>" }`;

      const response = await fetch(`${apiBase}/api/marketing/ai/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: 'openai',
          model_name: 'gpt-4o-mini',
          prompt
        })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to generate content');
      }

      const result = await response.json();
      let generatedText = '';

      if (typeof result?.data === 'string') {
        generatedText = result.data;
      } else if (result?.data?.text) {
        generatedText = result.data.text;
      } else if (result?.data?.raw) {
        generatedText = result.data.raw;
      } else {
        generatedText = JSON.stringify(result?.data ?? {});
      }

      generatedText = (generatedText || '').trim();
      if (!generatedText) {
        throw new Error('Empty response from AI');
      }

      setPostText(generatedText);
      toast.success('Content generated! Review and edit as needed.');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Post to Facebook
  const handlePostNow = async () => {
    if (!postText.trim()) {
      toast.error('Please enter post text');
      return;
    }

    if (postText.length > 63206) {
      toast.error('Post text is too long (max 63,206 characters)');
      return;
    }

    setIsPosting(true);
    try {
      // Call the backend posting script
      const response = await fetch('/api/post-to-social', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platform: 'facebook',
          pageId: selectedCompany.pageId,
          text: postText,
          imageUrl: imageUrl || undefined,
          linkUrl: linkUrl || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to post to Facebook');
      }

      const result = await response.json();
      
      if (result.success) {
        toast.success(`Posted successfully to ${selectedCompany.name}! 🎉`);
        // Clear form
        setPostText('');
        setImageUrl('');
        setLinkUrl('');
      } else {
        toast.error(`Failed to post: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error posting:', error);
      toast.error('Failed to post to Facebook. Check console for details.');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Facebook className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Facebook Manager</h1>
        </div>
        <p className="text-gray-600">AI-powered Facebook posting for all your HVAC companies</p>
      </div>

      {/* Status Banner */}
      <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
              Facebook API Connected
            </h3>
            <p className="text-sm text-gray-600">
              {companies.length} Pages connected and ready to post
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Selected Page</div>
            <div className="font-semibold text-gray-900">{selectedCompany.name}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'create', label: 'Create & Post', icon: Send },
            { id: 'analytics', label: 'Analytics', icon: Calendar }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'create' && (
          <CreatePostTab
            companies={companies}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            postText={postText}
            setPostText={setPostText}
            imageUrl={imageUrl}
            setImageUrl={setImageUrl}
            linkUrl={linkUrl}
            setLinkUrl={setLinkUrl}
            charCount={charCount}
            isGenerating={isGenerating}
            isGeneratingImage={isGeneratingImage}
            isPosting={isPosting}
            onGenerate={handleGenerateContent}
            onGenerateImage={handleGenerateImage}
            onPost={handlePostNow}
          />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CREATE POST TAB
// ============================================================================

interface CreatePostTabProps {
  companies: Company[];
  selectedCompany: Company;
  setSelectedCompany: (company: Company) => void;
  postText: string;
  setPostText: (text: string) => void;
  imageUrl: string;
  setImageUrl: (url: string) => void;
  linkUrl: string;
  setLinkUrl: (url: string) => void;
  charCount: number;
  isGenerating: boolean;
  isGeneratingImage: boolean;
  isPosting: boolean;
  onGenerate: () => void;
  onGenerateImage: () => void;
  onPost: () => void;
}

function CreatePostTab({
  companies,
  selectedCompany,
  setSelectedCompany,
  postText,
  setPostText,
  imageUrl,
  setImageUrl,
  linkUrl,
  setLinkUrl,
  charCount,
  isGenerating,
  isGeneratingImage,
  isPosting,
  onGenerate,
  onGenerateImage,
  onPost
}: CreatePostTabProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Composer */}
      <div className="lg:col-span-2 space-y-6">
        {/* Page Selector */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Facebook className="w-5 h-5 text-blue-600" />
            Select Facebook Page
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {companies.map((company) => (
              <button
                key={company.id}
                onClick={() => setSelectedCompany(company)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedCompany.id === company.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="font-semibold text-gray-900">{company.name}</div>
                <div className="text-xs text-gray-500 mt-1">Page ID: {company.pageId}</div>
              </button>
            ))}
          </div>
        </div>

        {/* AI Generation */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            AI Generators
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Let AI create engaging content and professional images for {selectedCompany.name}.
          </p>
          <div className="space-y-3">
            <button
              onClick={onGenerate}
              disabled={isGenerating}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Generating Text...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Post Text
                </>
              )}
            </button>
            <button
              onClick={onGenerateImage}
              disabled={isGeneratingImage}
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              {isGeneratingImage ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  Generating Image... (30-120s)
                </>
              ) : (
                <>
                  <ImageIcon className="w-5 h-5" />
                  Generate Image with Maginary
                </>
              )}
            </button>
          </div>
        </div>

        {/* Post Composer */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Type className="w-5 h-5 text-gray-600" />
            Compose Post
          </h2>
          
          <div className="space-y-4">
            {/* Post Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Post Text
              </label>
              <textarea
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                placeholder="What would you like to share with your audience?"
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500">
                  Facebook allows up to 63,206 characters
                </span>
                <span className={`text-sm font-medium ${
                  charCount > 63206 ? 'text-red-600' : 
                  charCount > 500 ? 'text-yellow-600' : 
                  'text-gray-600'
                }`}>
                  {charCount.toLocaleString()} characters
                </span>
              </div>
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Image URL (Optional)
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Publicly accessible URL to an image. Posts with images get 2.3x more engagement!
              </p>
            </div>

            {/* Link URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link URL (Optional)
              </label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com/your-page"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Link to a blog post, promotion, or landing page
              </p>
            </div>

            {/* Post Button */}
            <button
              onClick={onPost}
              disabled={isPosting || !postText.trim()}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-lg"
            >
              {isPosting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Posting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Post to {selectedCompany.name}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Right Column - Preview & Tips */}
      <div className="space-y-6">
        {/* Post Preview */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Post Preview</h2>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                <Facebook className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">{selectedCompany.name}</div>
                <div className="text-xs text-gray-500">Just now</div>
              </div>
            </div>
            <div className="text-gray-800 whitespace-pre-wrap text-sm">
              {postText || 'Your post will appear here...'}
            </div>
            {imageUrl && (
              <div className="mt-3">
                <img 
                  src={imageUrl} 
                  alt="Post preview" 
                  className="w-full rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            {linkUrl && (
              <div className="mt-3 p-3 border border-gray-300 rounded bg-white">
                <div className="text-xs text-gray-500 uppercase mb-1">Link Preview</div>
                <div className="text-sm font-medium text-blue-600 truncate">{linkUrl}</div>
              </div>
            )}
          </div>
        </div>

        {/* Best Practices */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">📊 Best Practices</h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">Keep posts under 500 characters for maximum engagement</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">Include a clear call-to-action</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">Use high-quality images (1200x630px recommended)</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">Post between 1-3 PM for best reach</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold">✓</span>
              <span className="text-gray-700">Ask questions to increase engagement</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-red-600 font-bold">✗</span>
              <span className="text-gray-700">Avoid excessive hashtags (Facebook doesn't prioritize them)</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">📈 Quick Stats</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Connected Pages</span>
              <span className="font-semibold text-gray-900">{companies.length}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Character Limit</span>
              <span className="font-semibold text-gray-900">63,206</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">Recommended Length</span>
              <span className="font-semibold text-gray-900">&lt; 500</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ANALYTICS TAB
// ============================================================================

function AnalyticsTab() {
  return (
    <div className="space-y-6">
      <div className="card text-center py-12">
        <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics Coming Soon</h3>
        <p className="text-gray-600 mb-6">
          Facebook Insights integration will show post performance, reach, engagement, and audience demographics.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mt-8">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-400">--</div>
            <div className="text-sm text-gray-600 mt-1">Total Reach</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-400">--</div>
            <div className="text-sm text-gray-600 mt-1">Engagement Rate</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-400">--</div>
            <div className="text-sm text-gray-600 mt-1">Total Posts</div>
          </div>
        </div>
      </div>
    </div>
  );
}
